import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, Modal, ScrollView, StyleSheet, Text, View } from '@/ui';
import { BrandButton } from '@/components/ui/BrandButton';
import { useAuthStore } from '@/features/auth/authStore';
import { API_URL } from '@/lib/config';
import { tokens } from '@/theme/tokens';
import { useAppTheme, type ThemeColors } from '@/theme/ThemeProvider';
import { acceptPolicies, getConsents, type OutstandingPolicy } from '@/api/consents';

/**
 * SEC-18 — the re-acceptance prompt.
 *
 * NOT DISMISSIBLE, but also not a wall. It appears when the fan owes an
 * acceptance and it covers the screen, yet the app behind it stays fully usable
 * for READING once dismissed-by-accepting is refused: the server only gates
 * writes, so a fan who force-quits and reopens can still see their tickets. That
 * asymmetry is deliberate — the alternative locks somebody out of a ticket they
 * already paid for, at the turnstile, over a document change they had no part
 * in.
 *
 * The copy distinguishes a first acceptance from a re-acceptance, because
 * "please accept our terms" and "we've updated our terms" are different things
 * to say to somebody who has been a member for two years.
 */
export function ConsentGate() {
  const { t } = useTranslation('me');
  const { colors: C } = useAppTheme();
  const styles = useMemo(() => createStyles(C), [C]);
  const status = useAuthStore((s) => s.status);
  const token = useAuthStore((s) => s.accessToken);

  const [outstanding, setOutstanding] = useState<OutstandingPolicy[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (status !== 'authenticated' || !token) {
      setOutstanding([]);
      return;
    }
    try {
      const state = await getConsents(token);
      setOutstanding(state.outstanding);
    } catch {
      // Silent. A failed consent check must not produce an error banner on a
      // screen the fan did not ask to see — the server-side gate is the control,
      // and this prompt is the courtesy of asking before they hit it.
      setOutstanding([]);
    }
  }, [status, token]);

  useEffect(() => {
    void load();
  }, [load]);

  const accept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await acceptPolicies(token as string, outstanding.map((p) => p.id));
      setOutstanding([]);
    } catch {
      setError(t('consents.error', 'We could not record that. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  if (status !== 'authenticated' || outstanding.length === 0) return null;

  // Anyone who has accepted a previous version is being asked again, which is a
  // different message from a first-time acceptance.
  const isUpdate = outstanding.some((p) => !!p.accepted_version);

  return (
    <Modal visible transparent animationType="fade" onRequestClose={() => {}}>
      <View style={styles.backdrop}>
        <View style={styles.card}>
          <Text style={styles.title}>
            {isUpdate
              ? t('consents.updatedTitle', "We've updated our terms")
              : t('consents.firstTitle', 'Before you continue')}
          </Text>
          <Text style={styles.subtitle}>
            {isUpdate
              ? t(
                  'consents.updatedBody',
                  'Please review the changes. You can keep browsing, but you’ll need to accept before buying or transferring tickets.',
                )
              : t(
                  'consents.firstBody',
                  'Please review and accept these to use your account.',
                )}
          </Text>

          <ScrollView style={styles.list}>
            {outstanding.map((p) => (
              <View key={p.id} style={styles.row}>
                <Text style={styles.rowTitle}>
                  {p.title || p.kind} · v{p.version}
                </Text>
                {!!p.summary && <Text style={styles.rowSummary}>{p.summary}</Text>}
                {!!p.accepted_version && (
                  <Text style={styles.rowPrev}>
                    {t('consents.previously', 'You accepted v{{version}}', {
                      version: p.accepted_version,
                    })}
                  </Text>
                )}
                <Text
                  style={styles.link}
                  onPress={() => {
                    // The full text lives on a public route, so it opens without
                    // a session — a policy you must accept is one you must be
                    // able to read, including before you have an account.
                    void Linking.openURL(`${API_URL}/policies/${p.id}`);
                  }}
                >
                  {t('consents.read', 'Read the full text')}
                </Text>
              </View>
            ))}
          </ScrollView>

          {!!error && <Text style={styles.error}>{error}</Text>}

          <BrandButton onPress={() => void accept()} disabled={submitting}>
            {submitting
              ? t('consents.accepting', 'Saving…')
              : t('consents.accept', 'Accept and continue')}
          </BrandButton>
        </View>
      </View>
    </Modal>
  );
}

function createStyles(C: ThemeColors) {
  return StyleSheet.create({
    backdrop: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.6)',
      justifyContent: 'center',
      padding: tokens.space.lg,
    },
    card: {
      backgroundColor: C.surface,
      borderRadius: tokens.radius.lg,
      padding: tokens.space.lg,
      gap: tokens.space.md,
      maxHeight: '80%',
    },
    title: { color: C.text, fontSize: 20, fontWeight: '700' },
    subtitle: { color: C.textDim, fontSize: 14, lineHeight: 20 },
    list: { flexGrow: 0 },
    row: {
      paddingVertical: tokens.space.sm,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: C.border,
      gap: 4,
    },
    rowTitle: { color: C.text, fontSize: 15, fontWeight: '600' },
    rowSummary: { color: C.textDim, fontSize: 13, lineHeight: 18 },
    rowPrev: { color: C.textDim, fontSize: 12, fontStyle: 'italic' },
    link: { color: C.primary, fontSize: 13, fontWeight: '600' },
    // The palette has no danger token; the brand red is defined in tokens.
    error: { color: '#d9534f', fontSize: 13 },
  });
}
